import { ResponsiveBar } from "@nivo/bar";
import { Box, Text } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import React, { useEffect, useState } from "react";
import { CHART_COLORS, getChartTheme } from "./chartTheme";
import { makeRoundedBar, yTickValues } from "./chartHelpers";

export interface BarChartProps {
  data: any[];
  keys: string[];
  indexBy: string;
  layout?: "horizontal" | "vertical";
  /** Bar grouping: "stacked" (default) piles keys on top of each other; "grouped" places them side by side. */
  groupMode?: "stacked" | "grouped";
  height?: number;
  /**
   * When set, truncates the index-axis tick labels (left axis for horizontal,
   * bottom axis for vertical) to this many characters with an ellipsis. The
   * underlying `indexBy` value is left untouched, so the tooltip still shows the
   * full label. Omit to keep Nivo's default full-length labels.
   */
  maxLabelLength?: number;
  /**
   * Which end to cut when a label exceeds `maxLabelLength`. "end" (default) keeps the first
   * N chars ("GET /api/very/lon…"); "start" keeps the last N chars ("…/orders/refund"), which
   * is better for long endpoint paths where the distinguishing part is at the end.
   */
  truncateLabelFrom?: "start" | "end";
}

const truncateLabel = (
  value: string,
  max: number,
  from: "start" | "end",
): string => {
  if (value.length <= max) return value;
  return from === "start"
    ? `…${value.slice(value.length - (max - 1)).trimStart()}`
    : `${value.slice(0, max - 1).trimEnd()}…`;
};

// Fewer, rounded value-axis ticks + a subtly rounded bar top, to match the widget.
const Y_TICK_TARGET = 5;
// Subtle rounded bar top (matches the widget).
const BAR_RADIUS = 5;

const BarChart: React.FC<BarChartProps> = ({
  data,
  keys,
  indexBy,
  layout = "vertical",
  groupMode = "stacked",
  height = 250,
  maxLabelLength,
  truncateLabelFrom = "end",
}) => {
  const { t } = useTranslation();
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  if (!data || data.length === 0) {
    return (
      <Box
        h={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="gray.500">{t("app.common.noDataAvailable")}</Text>
      </Box>
    );
  }

  const isHorizontal = layout === "horizontal";
  const isStacked = groupMode === "stacked";

  // Value-axis domain: stacked bars reach the per-category total; grouped bars only
  // reach the largest single key value, so measure accordingly.
  const categoryExtents = isStacked
    ? data.map((row) => keys.reduce((sum, k) => sum + (Number(row[k]) || 0), 0))
    : data.flatMap((row) => keys.map((k) => Number(row[k]) || 0));
  const yTicks = yTickValues(categoryExtents, Y_TICK_TARGET);

  const roundedBar = makeRoundedBar(
    BAR_RADIUS,
    layout,
    getChartTheme(isDark).labels.text,
    keys,
    isStacked,
  );

  const truncateAxis = maxLabelLength
    ? {
        format: (v: any) =>
          truncateLabel(String(v), maxLabelLength, truncateLabelFrom),
      }
    : undefined;

  // The value axis is the left axis for vertical bars, the bottom axis for
  // horizontal bars; grid lines follow the same axis so they line up with ticks.
  const valueAxis = { tickValues: yTicks };

  // Nivo only styles its built-in tooltips; a custom tooltip element needs its own
  // (theme-aware) container background, matching the widget charts.
  const tooltipStyle: React.CSSProperties = {
    ...getChartTheme(isDark).tooltip.container,
    whiteSpace: "nowrap",
  };

  return (
    <Box height={`${height}px`}>
      <ResponsiveBar
        key={isDark ? "dark" : "light"}
        data={data}
        keys={keys}
        indexBy={indexBy}
        layout={layout}
        groupMode={groupMode}
        theme={getChartTheme(isDark)}
        colors={CHART_COLORS}
        margin={{
          top: 10,
          right: 20,
          bottom: 40,
          left: isHorizontal ? 120 : 50,
        }}
        animate={false}
        barComponent={roundedBar}
        enableGridX={isHorizontal}
        enableGridY={!isHorizontal}
        gridXValues={isHorizontal ? yTicks : undefined}
        gridYValues={isHorizontal ? undefined : yTicks}
        axisLeft={isHorizontal ? truncateAxis : valueAxis}
        axisBottom={isHorizontal ? valueAxis : truncateAxis}
        tooltip={({ id, value, indexValue, color }) => (
          <div style={tooltipStyle}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              {String(indexValue)}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: color,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span>{keys.length > 1 ? `${id}: ${value}` : value}</span>
            </div>
          </div>
        )}
      />
    </Box>
  );
};

export default BarChart;
